import { OfferingsProps, ServicesProps } from '@jbhi-fi/apis/src/services/types';
import { ServicesInfoBlockProps } from '@jbhi-fi/components/src/Attach/ServicesInfoBlock/ServicesInfoBlock';
import { messaging } from './consts';
import React from 'react';
import { formatMoney } from '../../../../utils/FormatMoney';
import { ProductServiceModalProps } from '@jbhi-fi/components/src/Attach/ProductServiceModal/ProductServiceModal';
import { InfoCalloutBlock } from '@jbhi-fi/components/src/Attach/InfoCalloutBlock/InfoCalloutBlock';
import { ServiceIds } from '../../../../utils/constants/services';
import {
  generateHeadingFeatures,
  generateIncludedWarranties,
  splitDisclosureText,
} from '@jbhi-fi/components/src/Attach/utils/utils';

export const elementIds = {
  [ServiceIds.ExtraCare]: 'ec-cta',
  [ServiceIds.MobileExtras]: 'me-cta',
  [ServiceIds.ExtraCareNz]: 'ec-nz-cta',
  [ServiceIds.AppleCareNz]: 'ac-nz-cta',
};

export const getServiceButtonHeading = (offering: OfferingsProps, isCompare: boolean) => {
  const cleanName = offering.name?.replace('Mobile Extras ', '').replace('for iPad ', '');
  const duration = offering.suggestedWarrantyPeriods?.find(warranty => !warranty.isIncludedWithProduct)?.period;
  const compareLabel = duration ? `${duration} additional cover` : cleanName;
  return isCompare ? compareLabel : cleanName;
};

export const generateServicesInfoBlockProps = ({
  heading,
  onAdd,
  onRemove,
  selectedOfferingId,
  service,
  isExtraCare,
  isExtraCareReplacementCover,
  isAppleCareNz,
  buttonPosition,
  isSlideoutOrCart,
  isSlideout,
  isCartReady,
  shouldShowActionButtons,
}: {
  heading: string;
  onAdd: (event: React.ChangeEvent | React.SyntheticEvent, offering?: OfferingsProps) => void;
  onRemove: (service?: ServicesProps, offeringId?: string, shouldRemoveFromCart?: boolean) => Promise<void>;
  selectedOfferingId?: string;
  service: ServicesProps;
  isExtraCare?: boolean;
  isExtraCareReplacementCover?: boolean;
  isAppleCareNz?: boolean;
  buttonPosition: 'bottom' | 'right';
  isSlideoutOrCart?: boolean;
  isSlideout?: boolean;
  isCartReady?: boolean;
  shouldShowActionButtons?: boolean;
}): ServicesInfoBlockProps | undefined => {
  const baseProps = {
    heading,
    service: service,
    selectedOfferingId,
    onAdd: (event: React.ChangeEvent | React.SyntheticEvent, offeringId?: string) =>
      onAdd(
        event,
        service?.offerings?.find(offering => offering?.id === offeringId)
      ),
    onRemove: async (offeringId?: string) => await onRemove(service, offeringId ?? '', isSlideoutOrCart),
    buttonPosition: buttonPosition,
    isCartReady,
    subText: '',
    shouldShowActionButtons,
  };
  baseProps.subText = isExtraCare
    ? isExtraCareReplacementCover
      ? messaging.extraCare.replacementHeading
      : messaging.extraCare.repairHeading
    : '';

  if (!service.offerings) return;

  const extraServiceInfoProps = {
    headingLogoURL: service.imageUrl,
    isExtraCare,
    features: service.offerings[0]?.shortServiceFeatures,
    isSlideout,
    offerings: service?.offerings?.map(offering => {
      return {
        ...enrichExtraCareOffering({
          offering,
          isExtraCare,
          isExtraCareReplacementCover: isExtraCare ? isExtraCareReplacementCover : undefined,
          isAppleCareNz,
        }),
        ...offering,
      };
    }),
  };

  const serviceInfo = {
    ...baseProps,
    ...extraServiceInfoProps,
  };
  return serviceInfo;
};

const enrichExtraCareOffering = ({
  offering,
  isExtraCareReplacementCover,
  isExtraCare,
  isAppleCareNz,
}: {
  offering: OfferingsProps;
  isExtraCareReplacementCover?: boolean;
  isExtraCare?: boolean;
  isAppleCareNz?: boolean;
}) => {
  let offeringLength =
    offering?.suggestedWarrantyPeriods
      ?.find(swp => swp?.name?.includes('JB Hi-Fi Extra Care:'))
      ?.period?.replace('(s)', 's') ?? '';
  if (isAppleCareNz) {
    const offeringLengthNumber = parseInt(offering?.additionalCoveragePeriod?.match(/\d+/)?.[0] ?? '0') * 12;
    offeringLength = offeringLengthNumber ? `${offeringLengthNumber}-month` : '';
  }
  const offeringName =
    offering?.name?.replace(isExtraCare ? 'JB Hi-Fi ' : 'JB Hi-Fi Extras', '').replace(':', '') ?? '';

  let subText = '';
  if (isExtraCareReplacementCover) {
    subText = 'Starts 12 months after delivery/collection.';
  } else if (isExtraCare) {
    subText = "Starts after the expiry of the manufacturer's warranty and JB Hi-Fi's Minimum Voluntary Warranty.";
  }

  return {
    heading: isAppleCareNz
      ? `Add ${offeringLength} plan ${formatMoney(offering.priceInc)}`
      : `Add ${offeringLength} ${offeringName} ${formatMoney(offering.priceInc)}`,
    subText: subText,
    features: [],
  };
};

export const generateProductServiceModalProps = ({
  service,
  offering,
  onAdd,
  onRemove,
  selectedOfferingId,
  isExtraCare,
  isExtraCareReplacementCover,
  isAppleCareNz,
  isInlineModal,
  isSlideoutOrCart,
  isCartReady,
}: {
  service: ServicesProps;
  offering: OfferingsProps;
  onAdd: (service?: ServicesProps, offeringId?: string) => Promise<void>;
  onRemove: (service?: ServicesProps, offeringId?: string, shouldRemoveFromCart?: boolean) => Promise<void>;
  selectedOfferingId?: string;
  isExtraCare?: boolean;
  isExtraCareReplacementCover?: boolean;
  isAppleCareNz?: boolean;
  shouldShowAlternateOffering?: boolean;
  isInlineModal?: boolean;
  isSlideoutOrCart?: boolean;
  isCartReady?: boolean;
  preSelectedOfferingId?: string;
}): ProductServiceModalProps => {
  const baseProps = {
    heading: `${offering.shortName ? offering.shortName.replace(':', '') : ''}`,
    headingBlock: (
      <InfoCalloutBlock
        flexDirection="row"
        features={generateHeadingFeatures({ offering, isExtraCare, isExtraCareReplacementCover })}
      />
    ),
    features: offering.serviceFeatures ? offering.serviceFeatures.map(feature => feature.description ?? '') : undefined,
    ...splitDisclosureText(offering.disclosureText),
    priceInc: offering.priceInc,
    selectedOfferingId: isInlineModal
      ? selectedOfferingId
      : selectedOfferingId === offering.id
      ? selectedOfferingId
      : undefined,
    onAdd: async (event: React.ChangeEvent | React.SyntheticEvent, offeringId?: string) =>
      await onAdd(service, offeringId ?? offering.id),
    onRemove: async (event: React.ChangeEvent | React.SyntheticEvent, offeringId?: string) => {
      await onRemove(service, offeringId ?? offering.id, isSlideoutOrCart);
    },
    includedWarranties: isExtraCare ? generateIncludedWarranties(offering) : undefined,
    isCondensed: isInlineModal,
    isCartReady,
  };

  return isExtraCare && !isAppleCareNz
    ? {
        ...baseProps,
        shortName: 'Extra Care',
        ...(isInlineModal
          ? {
              disclosureTextPositions: {
                disclosureTextPosition: 'bottom-left',
                addToCartDisclosureTextPosition: 'top-right',
              },
            }
          : {}),
      }
    : isAppleCareNz
    ? {
        ...baseProps,
        shortName: 'AppleCare+',
        ...(isInlineModal
          ? {
              disclosureTextPositions: {
                disclosureTextPosition: 'bottom-left',
                addToCartDisclosureTextPosition: 'bottom-left',
              },
            }
          : {}),
      }
    : {
        ...baseProps,
        shortName: 'JB Hi-Fi Extras',
        headingBlock: isInlineModal ? undefined : baseProps.headingBlock,
        ...(isInlineModal
          ? {
              disclosureTextPositions: {
                disclosureTextPosition: 'bottom-left',
                addToCartDisclosureTextPosition: 'bottom-left',
              },
            }
          : {}),
      };
};
